/**
 * 選択中の商品 (ASIN / 手動商品) に紐づく画像素材と選択中素材を照らし合わせ、欠けている画像を戻す
 * (SelectedAssetsPanel の「商品画像を復元」。2026-10-22 利用者指示)。
 *
 * - 商品の画像一覧は一時素材の一覧 API を商品で絞って 1 回取る (react-query。キーは 'temporary-materials' 配下なので、
 *   useProductMaterials が商品画像を取り込んだあとの refetchQueries で一緒に更新される)
 * - 商品に紐づく素材には商品の文脈で生成した動画・絵コンテ・BGM・アップロードなども含まれるため、取得元 (source_type) が
 *   商品写真そのもの (asin = Amazon 商品画像 / manual_product = 案件登録画像) だけを見る (url / upload は対象外。2026-10-22 利用者決定)。手動商品の写真はシナリオごとに取り込み直されて同じ元 URL の複製が
 *   増えるので、元 URL (source_url) ごとに 1 枚にまとめる (復元は最新の 1 枚)
 * - 欠けている = まとめた写真のうち、どの複製も選択中素材に無いもの。復元は addAsset で戻すだけ (通信なし)
 * - 商品が選択されていなければ何もしない (ボタンも出ない)
 */
import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchTemporaryMaterials, type MaterialFilters } from '../../../features/materials/api';
import type { TemporaryMaterial } from '../../../features/materials/materialsSlice';
import { buildDragData, resolveMaterialUrls } from '../../../features/materials/materialUrl';
import { useSelectedProductStore } from '../../../stores/selectedProductStore';
import { useToast } from '../../../hooks/useToast';
import { useGenerationAssets } from '../state/assetsContext';
import type { GenerationAsset } from '../state/types';

const PRODUCT_IMAGE_LIST_LIMIT = 200;
/** 商品写真とみなす取得元: Amazon 商品画像 (asin) と案件登録画像 (manual_product) だけ。URL 取得・アップロードは含めない */
export const PRODUCT_IMAGE_SOURCE_TYPES = ['asin', 'manual_product'];

export function productImageFilters(ref: { asin: string | null; manualProductId: string | null }): MaterialFilters | null {
  const common: MaterialFilters = { file_types: ['image'], source_types: [...PRODUCT_IMAGE_SOURCE_TYPES] };
  if (ref.asin) return { ...common, asin: ref.asin };
  if (ref.manualProductId) return { ...common, product_id: ref.manualProductId, product_type: 'manual' };
  return null;
}

type ProductImageLike = Pick<TemporaryMaterial, 'id'> & {
  content_type?: string | null;
  source_type?: string | null;
  source_url?: string | null;
  created_at?: string | null;
};

/**
 * 欠けている商品写真 (純粋関数。テスト用に分離)。
 * 同じ元 URL の複製は 1 つにまとめ、その複製のどれも選択中素材に無いときだけ「欠けている」とし、代表として最新の 1 枚を返す。
 * 元 URL が無い素材は id 単位で扱う。
 */
export function findMissingProductImages<T extends ProductImageLike>(
  productImages: ReadonlyArray<T>,
  selectedAssets: ReadonlyArray<Pick<GenerationAsset, 'id'>>,
): T[] {
  const selected = new Set(selectedAssets.map((a) => a.id));
  const groups = new Map<string, T[]>();
  for (const m of productImages) {
    if (!((m.content_type ?? '') as string).startsWith('image/')) continue;
    if (m.source_type && !PRODUCT_IMAGE_SOURCE_TYPES.includes(m.source_type)) continue;
    const key = m.source_url ? `url:${m.source_url}` : `id:${m.id}`;
    const list = groups.get(key);
    if (list) list.push(m); else groups.set(key, [m]);
  }
  const out: T[] = [];
  for (const list of groups.values()) {
    if (list.some((m) => selected.has(m.id))) continue;
    const newest = [...list].sort((a, b) => String(b.created_at ?? '').localeCompare(String(a.created_at ?? '')))[0];
    out.push(newest);
  }
  return out;
}

export function useProductImageCompletion(selectedAssets: ReadonlyArray<GenerationAsset>) {
  const asin = useSelectedProductStore((s) => s.asin);
  const manualProductId = useSelectedProductStore((s) => s.manualProductId);
  const { addAsset } = useGenerationAssets();
  const toast = useToast();
  const filters = useMemo(() => productImageFilters({ asin, manualProductId }), [asin, manualProductId]);

  const { data } = useQuery({
    queryKey: ['temporary-materials', 'product-image-set', asin ?? '', manualProductId ?? ''],
    enabled: filters != null,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await fetchTemporaryMaterials({ limit: PRODUCT_IMAGE_LIST_LIMIT, filters: filters! });
      return res.items ?? [];
    },
  });

  const missing = useMemo(() => findMissingProductImages(data ?? [], selectedAssets), [data, selectedAssets]);

  const restore = useCallback(() => {
    if (missing.length === 0) return;
    for (const material of missing) {
      const urls = resolveMaterialUrls(material);
      // buildDragData は素材パネルからの D&D と同じ形 (GenerationAsset 相当) を作る
      addAsset({ ...(buildDragData(material, urls) as unknown as GenerationAsset), hasProductLinks: true });
    }
    toast.show(`${missing.length}枚の画像を選択中素材に戻しました`);
  }, [addAsset, missing, toast]);

  return { missingCount: missing.length, restore };
}
